import { Box, Text } from '@bonanza-social/moonlight';
import { useTranslation } from '@bonanza-social/i18n';
import { memo, useMemo } from 'react';
import StatusGemWithOutline from '../StatusGemWithOutline/StatusGemWithOutline';
import { StatusHorizontalProgressProps } from './StatusHorizontalProgress.types';
import RetainStatusLabel from '../RetainStatusLabel/RetainStatusLabel';
import { formatNumber } from '@bonanza-social/toolkit/utils';

function StatusHorizontalProgress({
  currentStatus,
  nextStatus,
  claimedBoxesCount,
  promotionNumberOfBoxes,
  keepStatusNumberOfBoxes,
  nextStatusRewardAmount,
}: StatusHorizontalProgressProps) {
  const [t] = useTranslation('status');

  const gemsArray = useMemo(() => {
    return [...Array(promotionNumberOfBoxes).keys()].map((i) => i + 1);
  }, [promotionNumberOfBoxes]);

  const sectorsArray = useMemo(() => {
    return [
      ...Array(promotionNumberOfBoxes && promotionNumberOfBoxes - 1).keys(),
    ].map((i) => i + 1);
  }, [promotionNumberOfBoxes]);

  if (!promotionNumberOfBoxes) {
    return null;
  }

  return (
    <Box width="100%">
      <RetainStatusLabel
        keepStatusNumberOfBoxes={keepStatusNumberOfBoxes}
        promotionNumberOfBoxes={promotionNumberOfBoxes}
        numberOfSectors={sectorsArray.length}
      />
      <Box
        flexDirection="row"
        position="relative"
        justifyContent="space-between"
      >
        {gemsArray.map((gem) => {
          const isNextGem = nextStatus && gem === promotionNumberOfBoxes;
          const isActive =
            (claimedBoxesCount && gem <= claimedBoxesCount) || isNextGem;

          return (
            <StatusGemWithOutline
              key={`gem-${gem}`}
              playerStatus={isNextGem ? nextStatus : currentStatus}
              size="space-800"
              outlineColor={
                gem === claimedBoxesCount ? 'iconAccentSolid' : 'iconOutline'
              }
              isLocked={!isActive}
            />
          );
        })}

        <Box
          flexDirection="row"
          position="absolute"
          left="space-400"
          right="space-400"
          top="space-0"
          bottom="space-0"
          alignItems="center"
          zIndex={-10}
        >
          {sectorsArray.map((sector) => {
            const isBeforeHighlightedGem =
              claimedBoxesCount && sector === claimedBoxesCount - 1;
            const isAfterHighlightedGem =
              claimedBoxesCount && sector === claimedBoxesCount;

            return (
              <Box
                position="relative"
                key={`sector-${sector}`}
                height="space-050"
                flex={1}
                backgroundColor={
                  isBeforeHighlightedGem || isAfterHighlightedGem
                    ? 'accentSolidBackground'
                    : 'secondaryBackground'
                }
              >
                {isBeforeHighlightedGem || isAfterHighlightedGem ? (
                  <Box
                    position="absolute"
                    left="space-0"
                    right="space-0"
                    top="space-0"
                    bottom="space-0"
                    variant="gradient"
                    gradient="secondaryLeftOverlayBackground"
                    transform={`scaleX(${isBeforeHighlightedGem ? -1 : 1})`}
                  />
                ) : null}
              </Box>
            );
          })}
        </Box>
      </Box>
      {/* <NextStatusLabel> */}
      {nextStatus ? (
        <Box
          position="relative"
          top="-space-100"
          alignSelf="flex-end"
          variant="gradient"
          gradient={`status${nextStatus}Background`}
          paddingHorizontal="space-100"
          paddingVertical="space-050"
          borderRadius="radius-100"
        >
          <Text
            locNS="status"
            locKey={nextStatus}
            variant="badge"
            color="darkSurfaceAccent"
          />
        </Box>
      ) : null}
      {/* </NextStatusLabel> */}
      {/* <NextGemReward> */}
      {nextStatusRewardAmount ? (
        <Box
          paddingTop={nextStatus ? 'space-100' : 'space-500'}
          alignSelf="center"
        >
          <Text variant="badge" color="darkSurfacePrimary">
            {t('nextGemCoinsReward', {
              amount: formatNumber(nextStatusRewardAmount),
            })}
          </Text>
        </Box>
      ) : null}

      {/* </NextGemReward> */}
    </Box>
  );
}

export default memo(StatusHorizontalProgress);
